import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Router,
  Trash2,
  Pencil,
  RefreshCw,
  Cpu,
  HardDrive,
  Thermometer,
  Users,
  Globe,
  Network,
  Lock,
  Unlock,
  AlertCircle,
  CheckCircle2,
  Server,
  ArrowDown,
  ArrowUp,
  Loader2,
  ChevronDown,
  Activity,
  UserPlus,
  UserCheck,
  UserX,
  UserMinus,
  Search,
  User,
  Signal,
  ExternalLink,
  Clock,
} from "lucide-react";
import { AddMikrotikRouterModal } from "./AddMikrotikRouterModal";
import { CreatePppoeSecretModal } from "./CreatePppoeSecretModal";
import { useAuth } from "../../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const fmtBytes = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return (v / 1e9).toFixed(2) + " GB";
  if (v >= 1e6) return (v / 1e6).toFixed(1) + " MB";
  if (v >= 1e3) return (v / 1e3).toFixed(0) + " KB";
  return v + " B";
};

const fmtUptime = (u) => {
  if (!u) return "";
  const m = String(u).match(/^(?:(\d+)w)?(?:(\d+)d)?(?:(\d+):)?(\d+):(\d+)$/);
  if (!m) return u;
  const [, w, d, h, min] = m;
  const parts = [];
  if (w) parts.push(`${w} mgg`);
  if (d) parts.push(`${d} hari`);
  if (h) parts.push(`${h} jam`);
  parts.push(`${min} mnt`);
  return parts.join(" ");
};

const fmtDurasiOffline = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms) || ms < 0) return null;
  const m = Math.floor(ms / 60000);
  if (m < 1) return "baru saja";
  if (m < 60) return `${m} menit`;
  const j = Math.floor(m / 60);
  if (j < 48) return `${j} jam ${m % 60} mnt`;
  const d = Math.floor(j / 24);
  return `${d} hari ${j % 24} jam`;
};

// Maps a client PPPoE IP to its remote-modem URL using the router's segment config.
// Example: 10.10.10.70 with pool 10.10.10.2-... and port 8002:... => http://<public_ip>:8070
const _ipToInt = (ip) => {
  const p = String(ip || "").trim().split(".");
  if (p.length !== 4) return NaN;
  const nums = p.map((n) => parseInt(n, 10));
  if (nums.some((n) => Number.isNaN(n) || n < 0 || n > 255)) return NaN;
  return ((nums[0] << 24) >>> 0) + (nums[1] << 16) + (nums[2] << 8) + nums[3];
};

export const resolveRemoteUrl = (clientIp, cfg) => {
  if (!cfg || cfg.enabled === false || !Array.isArray(cfg.segments)) return null;
  const ci = _ipToInt(clientIp);
  if (Number.isNaN(ci)) return null;
  for (const seg of cfg.segments) {
    if (!seg || !seg.public_ip || !seg.ip_pool || !seg.port_range) continue;
    const [startIp, endIp] = String(seg.ip_pool).split("-").map((s) => s.trim());
    const si = _ipToInt(startIp);
    const ei = _ipToInt(endIp);
    if (Number.isNaN(si) || Number.isNaN(ei)) continue;
    if (ci >= si && ci <= ei) {
      const portStart = parseInt(String(seg.port_range).split(/[:\-]/)[0].trim(), 10);
      if (Number.isNaN(portStart)) continue;
      const port = portStart + (ci - si);
      return `http://${String(seg.public_ip).trim()}:${port}`;
    }
  }
  return null;
};

const RouterDetailPanel = ({ router, onRefreshList, onEdit, onDelete, canManage = true }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [snapError, setSnapError] = useState("");
  const [togglingId, setTogglingId] = useState(null);
  const [activeTab, setActiveTab] = useState("system"); // system | pppoe
  const [showCreateSecret, setShowCreateSecret] = useState(false);
  const [pppoeFilter, setPppoeFilter] = useState("all"); // all | active | offline | disabled
  const [secretSearch, setSecretSearch] = useState("");

  const loadSnapshot = async () => {
    setLoadingSnap(true);
    setSnapError("");
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/jaringan/mikrotik/routers/${router.id}/snapshot`,
        { withCredentials: true }
      );
      setSnapshot(res.data.snapshot);
    } catch (err) {
      setSnapError(err.response?.data?.detail || "Gagal fetch data router");
      setSnapshot(null);
    } finally {
      setLoadingSnap(false);
    }
  };

  // Auto-load when router changes
  useEffect(() => {
    if (router?.id) loadSnapshot();
    // eslint-disable-next-line
  }, [router?.id]);

  const handleTogglePppoe = async (secretName, currentDisabled) => {
    if (!window.confirm(currentDisabled ? `Buka isolir PPPoE "${secretName}"?` : `Isolir PPPoE "${secretName}"? Pelanggan akan terputus dari internet.`)) return;
    setTogglingId(secretName);
    try {
      await axios.post(
        `${BACKEND_URL}/api/jaringan/mikrotik/routers/${router.id}/pppoe/${encodeURIComponent(secretName)}/toggle`,
        {},
        { params: { disable: !currentDisabled }, withCredentials: true }
      );
      await loadSnapshot();
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal toggle PPPoE");
    } finally {
      setTogglingId(null);
    }
  };

  const isOnline = router.last_status === "online";

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#0e1628] to-[#0a1120] border border-slate-800 shadow-xl overflow-hidden"
      data-testid={`router-detail-${router.id}`}
    >
      {/* Header with actions */}
      <div className="p-4 flex items-center justify-between gap-3 border-b border-slate-800">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
            isOnline ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"
          }`}>
            <Router className="w-6 h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-white truncate">{router.nama}</h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                isOnline
                  ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                  : "bg-rose-500/20 text-rose-300 border border-rose-500/40"
              }`}>
                {isOnline ? "ONLINE" : "OFFLINE"}
              </span>
            </div>
            <p className="text-[11px] font-mono text-slate-400 truncate">
              {router.connection_type === "vpn" ? (
                <Network className="w-3 h-3 inline mr-1" />
              ) : (
                <Globe className="w-3 h-3 inline mr-1" />
              )}
              {router.host}:{router.port} • {router.username}
              {router.use_ssl && <span className="ml-1 text-emerald-400">🔒 SSL</span>}
            </p>
            {router.last_identity && (
              <p className="text-[10px] text-slate-500 mt-0.5">
                Identity: <span className="text-cyan-400 font-mono">{router.last_identity}</span>
                {router.last_board && ` • Board: ${router.last_board}`}
                {router.last_version && ` • RouterOS ${router.last_version}`}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={loadSnapshot}
            disabled={loadingSnap}
            title="Refresh Data"
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-cyan-400 disabled:opacity-50"
            data-testid={`refresh-router-${router.id}`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingSnap ? "animate-spin" : ""}`} />
          </button>
          {canManage && (
          <button
            onClick={() => onEdit(router)}
            title="Edit Router"
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-amber-900/60 text-amber-400 hover:text-amber-300"
            data-testid={`edit-router-${router.id}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          )}
          {canManage && (
          <button
            onClick={() => {
              if (window.confirm(`Hapus router "${router.nama}"?`)) onDelete(router.id);
            }}
            title="Hapus Router"
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-900/60 text-slate-400 hover:text-rose-400"
            data-testid={`delete-router-${router.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-800 bg-slate-950/60 px-2" data-testid="router-tabs">
        <button
          onClick={() => setActiveTab("system")}
          data-testid="tab-system"
          className={`px-4 py-2.5 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${
            activeTab === "system"
              ? "border-cyan-400 text-cyan-300"
              : "border-transparent text-slate-400 hover:text-slate-200"
          }`}
        >
          <Activity className="w-3.5 h-3.5" />
          <span>Sistem & Traffic</span>
        </button>
        <button
          onClick={() => setActiveTab("pppoe")}
          data-testid="tab-pppoe"
          className={`px-4 py-2.5 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${
            activeTab === "pppoe"
              ? "border-emerald-400 text-emerald-300"
              : "border-transparent text-slate-400 hover:text-slate-200"
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          <span>Manajemen PPPoE</span>
          {snapshot && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-slate-800 text-[9px] font-mono">
              {snapshot.pppoe_secrets_count}
            </span>
          )}
        </button>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4 bg-slate-950/40">
        {loadingSnap && !snapshot && (
          <div className="flex items-center justify-center gap-2 py-10 text-slate-400 text-xs">
            <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
            <span>Fetching data langsung dari router...</span>
          </div>
        )}

        {snapError && (
          <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{snapError}</span>
          </div>
        )}

        {snapshot && activeTab === "system" && (
          <>
            {/* System info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800">
                <div className="flex items-center justify-center gap-1 text-[10px] text-slate-400 mb-1">
                  <Cpu className="w-3 h-3 text-cyan-400" /> CPU Load
                </div>
                <p className="font-mono font-bold text-white text-sm">{snapshot.system?.cpu_load}%</p>
              </div>
              <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800">
                <div className="flex items-center justify-center gap-1 text-[10px] text-slate-400 mb-1">
                  <HardDrive className="w-3 h-3 text-indigo-400" /> RAM
                </div>
                <p className="font-mono font-bold text-white text-sm">{snapshot.system?.memory_used_pct}%</p>
                <p className="text-[9px] text-slate-500">
                  {snapshot.system?.memory_free_mb} / {snapshot.system?.memory_total_mb} MB
                </p>
              </div>
              <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800">
                <div className="flex items-center justify-center gap-1 text-[10px] text-slate-400 mb-1">
                  <Thermometer className="w-3 h-3 text-rose-400" /> Suhu
                </div>
                <p className="font-mono font-bold text-white text-sm">
                  {snapshot.system?.temperature ? `${snapshot.system.temperature}°C` : "N/A"}
                </p>
              </div>
              <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800">
                <div className="flex items-center justify-center gap-1 text-[10px] text-slate-400 mb-1">
                  <Users className="w-3 h-3 text-emerald-400" /> PPPoE Aktif
                </div>
                <p className="font-mono font-bold text-emerald-400 text-sm">
                  {snapshot.pppoe_active_count}{" "}
                  <span className="text-[10px] text-slate-500">/ {snapshot.pppoe_secrets_count}</span>
                </p>
              </div>
            </div>

            <div className="text-[10px] text-slate-500 flex flex-wrap items-center justify-between gap-2 px-1">
              <span>Uptime: {snapshot.system?.uptime}</span>
              <span>Arch: {snapshot.system?.architecture}</span>
              <span className="text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Live data
              </span>
            </div>

            {/* Ether interfaces */}
            <div>
              <p className="text-[11px] text-slate-400 font-semibold mb-2 flex items-center gap-1">
                <Network className="w-3 h-3 text-cyan-400" />
                Interfaces Traffic (live monitor-traffic)
              </p>
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {(snapshot.interfaces || []).map((iface) => (
                  <div
                    key={iface.name}
                    className="flex items-center justify-between py-1.5 px-2.5 bg-slate-900/70 rounded-lg border border-slate-800/60"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          iface.status === "running" ? "bg-emerald-400" : "bg-slate-600"
                        }`}
                      />
                      <span className="text-[11px] font-mono text-slate-200 truncate">{iface.name}</span>
                      <span className="px-1.5 py-0.5 rounded bg-slate-800 text-[9px] text-slate-400 font-bold">
                        {iface.type}
                      </span>
                      {iface.disabled && <span className="text-[9px] text-rose-400">DISABLED</span>}
                    </div>
                    <div className="flex gap-2 text-[10px] font-mono flex-shrink-0">
                      <span className="text-cyan-400 flex items-center gap-0.5">
                        <ArrowDown className="w-2.5 h-2.5" /> {iface.rx_mbps}M
                      </span>
                      <span className="text-indigo-400 flex items-center gap-0.5">
                        <ArrowUp className="w-2.5 h-2.5" /> {iface.tx_mbps}M
                      </span>
                    </div>
                  </div>
                ))}
                {(snapshot.interfaces || []).length === 0 && (
                  <p className="text-[11px] text-slate-500 italic text-center py-2">
                    Tidak ada interface ditemukan
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {snapshot && activeTab === "pppoe" && (
          <>
            {/* PPPoE KPI */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pppoe-kpi">
              <button
                onClick={() => setPppoeFilter("all")}
                className={`p-3 rounded-lg text-left transition-all ${
                  pppoeFilter === "all"
                    ? "bg-slate-800 border border-slate-500 ring-1 ring-slate-400"
                    : "bg-slate-900 border border-slate-800 hover:border-slate-700"
                }`}
                data-testid="filter-all"
              >
                <div className="flex items-center gap-1 text-[10px] text-slate-400 mb-1">
                  <Users className="w-3 h-3 text-slate-300" /> Total Secret
                </div>
                <p className="font-mono font-bold text-white text-lg">{snapshot.pppoe_secrets_count}</p>
              </button>
              <button
                onClick={() => setPppoeFilter("active")}
                className={`p-3 rounded-lg text-left transition-all ${
                  pppoeFilter === "active"
                    ? "bg-emerald-950/60 border border-emerald-500 ring-1 ring-emerald-500"
                    : "bg-slate-900 border border-slate-800 hover:border-emerald-500/40"
                }`}
                data-testid="filter-active"
              >
                <div className="flex items-center gap-1 text-[10px] text-slate-400 mb-1">
                  <UserCheck className="w-3 h-3 text-emerald-400" /> PPPoE Aktif
                </div>
                <p className="font-mono font-bold text-emerald-300 text-lg">
                  {snapshot.pppoe_active_count}
                </p>
              </button>
              <button
                onClick={() => setPppoeFilter("offline")}
                className={`p-3 rounded-lg text-left transition-all ${
                  pppoeFilter === "offline"
                    ? "bg-amber-950/60 border border-amber-500 ring-1 ring-amber-500"
                    : "bg-slate-900 border border-slate-800 hover:border-amber-500/40"
                }`}
                data-testid="filter-offline"
              >
                <div className="flex items-center gap-1 text-[10px] text-slate-400 mb-1">
                  <UserMinus className="w-3 h-3 text-amber-400" /> PPPoE Offline
                </div>
                <p className="font-mono font-bold text-amber-300 text-lg">
                  {snapshot.pppoe_offline_count ?? Math.max(0, snapshot.pppoe_secrets_count - snapshot.pppoe_active_count - (snapshot.pppoe_disabled_count || 0))}
                </p>
              </button>
              <button
                onClick={() => setPppoeFilter("disabled")}
                className={`p-3 rounded-lg text-left transition-all ${
                  pppoeFilter === "disabled"
                    ? "bg-rose-950/60 border border-rose-500 ring-1 ring-rose-500"
                    : "bg-slate-900 border border-slate-800 hover:border-rose-500/40"
                }`}
                data-testid="filter-disabled"
              >
                <div className="flex items-center gap-1 text-[10px] text-slate-400 mb-1">
                  <UserX className="w-3 h-3 text-rose-400" /> Isolir / Disabled
                </div>
                <p className="font-mono font-bold text-rose-300 text-lg">
                  {snapshot.pppoe_disabled_count || 0}
                </p>
              </button>
            </div>

            {/* Search & Create */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={secretSearch}
                  onChange={(e) => setSecretSearch(e.target.value)}
                  placeholder="Cari username / nama pelanggan / no. layanan / profile..."
                  className="w-full pl-9 pr-3 py-1.5 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  data-testid="secret-search-input"
                />
              </div>
              <button
                onClick={() => setShowCreateSecret(true)}
                className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-600/30"
                data-testid="create-secret-btn"
              >
                <UserPlus className="w-3.5 h-3.5" />
                <span>Buat Secret</span>
              </button>
            </div>

            {/* Secrets list filtered */}
            {(() => {
              const activeNames = new Set((snapshot.pppoe_active || []).map((a) => a.name));
              const activeAddrMap = snapshot.pppoe_active_addrs
                ? { ...snapshot.pppoe_active_addrs }
                : {};
              if (!snapshot.pppoe_active_addrs) {
                (snapshot.pppoe_active || []).forEach((a) => {
                  if (a.name) activeAddrMap[a.name] = a.address;
                });
              }
              const remoteCfg = router.remote_modem || {};
              let list = snapshot.pppoe_secrets || [];

              const isSecretOnline = (s) => (typeof s.online === "boolean" ? s.online : activeNames.has(s.name));
              if (pppoeFilter === "active") list = list.filter((s) => isSecretOnline(s) && !s.disabled);
              else if (pppoeFilter === "offline") list = list.filter((s) => !isSecretOnline(s) && !s.disabled);
              else if (pppoeFilter === "disabled") list = list.filter((s) => s.disabled);

              if (secretSearch.trim()) {
                const q = secretSearch.toLowerCase();
                list = list.filter(
                  (s) =>
                    s.name?.toLowerCase().includes(q) ||
                    s.comment?.toLowerCase().includes(q) ||
                    s.profile?.toLowerCase().includes(q) ||
                    s.pelanggan?.nama?.toLowerCase().includes(q) ||
                    s.pelanggan?.no_layanan?.toLowerCase().includes(q) ||
                    s.pelanggan?.no_hp?.includes(q)
                );
              }

              return (
                <div>
                  <p className="text-[11px] text-slate-400 font-semibold mb-2 flex items-center gap-1 flex-wrap">
                    <span>Menampilkan {list.length} dari {snapshot.pppoe_secrets_count} secret</span>
                    {snapshot.linked_pelanggan_count !== undefined && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 text-[10px] font-bold">
                        <User className="w-2.5 h-2.5 inline mr-0.5" />
                        {snapshot.linked_pelanggan_count} terhubung ke pelanggan
                      </span>
                    )}
                    {pppoeFilter !== "all" && (
                      <button
                        onClick={() => setPppoeFilter("all")}
                        className="ml-2 text-cyan-400 hover:underline text-[10px]"
                      >
                        Reset Filter
                      </button>
                    )}
                  </p>
                  <div className="max-h-[420px] overflow-y-auto space-y-1" data-testid="secrets-list">
                    {list.length === 0 ? (
                      <p className="text-[11px] text-slate-500 italic text-center py-4">Tidak ada secret sesuai filter</p>
                    ) : (
                      list.map((s) => {
                        const isActive = typeof s.online === "boolean" ? s.online : activeNames.has(s.name);
                        const clientIp = activeAddrMap[s.name];
                        const remoteUrl = isActive ? resolveRemoteUrl(clientIp, remoteCfg) : null;
                        return (
                          <div
                            key={s.name}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2.5 sm:py-2 px-3 bg-slate-900/70 rounded-lg border border-slate-800/60 text-[11px]"
                            data-testid={`secret-row-${s.name}`}
                          >
                            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1 flex-wrap">
                              <span
                                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                  s.disabled ? "bg-rose-400" : isActive ? "bg-emerald-400" : "bg-amber-400"
                                }`}
                                title={s.disabled ? "Disabled" : isActive ? "Aktif" : "Offline"}
                              />
                              <span className="text-slate-200 font-mono font-semibold truncate min-w-[80px]">{s.name}</span>
                              <span className="px-1.5 py-0.5 rounded bg-slate-800 text-[9px] text-cyan-300 font-bold">
                                {s.profile}
                              </span>
                              {s.disabled ? (
                                <span className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/30 text-[9px] font-bold">
                                  DISABLED
                                </span>
                              ) : isActive ? (
                                <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[9px] font-bold">
                                  ONLINE
                                </span>
                              ) : (
                                <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[9px] font-bold">
                                  OFFLINE
                                </span>
                              )}
                              {s.pelanggan ? (
                                <span
                                  className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-cyan-500/15 border border-cyan-500/40 text-cyan-200 text-[10px] font-semibold truncate max-w-full sm:max-w-[280px]"
                                  title={`${s.pelanggan.nama} · ${s.pelanggan.no_layanan}${s.pelanggan.no_hp ? " · " + s.pelanggan.no_hp : ""}`}
                                  data-testid={`secret-pelanggan-${s.name}`}
                                >
                                  <User className="w-2.5 h-2.5 text-cyan-400" />
                                  <span className="truncate">{s.pelanggan.nama}</span>
                                  <span className="text-cyan-400/70 font-mono text-[9px]">· {s.pelanggan.no_layanan}</span>
                                </span>
                              ) : s.comment ? (
                                <span className="text-[10px] text-slate-500 truncate hidden md:inline max-w-[200px]">· {s.comment}</span>
                              ) : (
                                <span className="text-[10px] text-slate-600 italic hidden md:inline">· belum terhubung</span>
                              )}
                              {s.pelanggan?.optical_power != null && (
                                <span
                                  className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border ${
                                    s.pelanggan.optical_power > -24
                                      ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                                      : s.pelanggan.optical_power >= -27
                                        ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
                                        : "bg-rose-500/15 border-rose-500/40 text-rose-300"
                                  }`}
                                  title={`Redaman ONU${s.pelanggan.onu_sn ? " · SN: " + s.pelanggan.onu_sn : ""}${s.pelanggan.odp_kode ? " · " + s.pelanggan.odp_kode : ""}`}
                                  data-testid={`pppoe-redaman-${s.name}`}
                                >
                                  <Signal className="w-2.5 h-2.5 inline mr-0.5" />
                                  {s.pelanggan.optical_power} dBm
                                </span>
                              )}
                              {isActive && clientIp && (
                                <a
                                  href={`http://${clientIp}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="px-2 py-1 sm:px-1.5 sm:py-0.5 rounded bg-sky-500/15 border border-sky-500/30 text-sky-300 hover:bg-sky-500/30 hover:text-white underline decoration-dotted underline-offset-2 text-[10px] font-mono font-semibold inline-flex items-center gap-1 transition-colors"
                                  data-testid={`pppoe-ip-${s.name}`}
                                  title={`Buka modem lewat jaringan lokal: http://${clientIp}`}
                                >
                                  {clientIp}
                                  <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                              )}
                              {isActive && s.uptime && (
                                <span
                                  className="ml-1 px-1.5 py-0.5 rounded bg-teal-500/15 border border-teal-500/30 text-teal-300 text-[10px] font-semibold"
                                  title="Lama sesi aktif (uptime)"
                                  data-testid={`pppoe-uptime-${s.name}`}
                                >
                                  <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                                  {fmtUptime(s.uptime)}
                                </span>
                              )}
                              {isActive && s.rx_bytes != null && (
                                <span
                                  className="ml-1 px-1.5 py-0.5 rounded bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 text-[10px] font-mono font-semibold"
                                  title="Total Download / Upload sesi ini"
                                  data-testid={`pppoe-traffic-${s.name}`}
                                >
                                  <ArrowDown className="w-2.5 h-2.5 inline mr-0.5" />{fmtBytes(s.rx_bytes)}
                                  <ArrowUp className="w-2.5 h-2.5 inline ml-1 mr-0.5" />{fmtBytes(s.tx_bytes)}
                                </span>
                              )}
                              {!isActive && !s.disabled && (
                                <span
                                  className="ml-1 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-semibold"
                                  title={s.offline_since ? `Terakhir online: ${new Date(s.offline_since).toLocaleString("id-ID")}` : "Belum pernah online / tidak diketahui"}
                                  data-testid={`pppoe-offline-since-${s.name}`}
                                >
                                  <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                                  {s.offline_since ? `Offline ${fmtDurasiOffline(s.offline_since)}` : "Offline (durasi tdk diketahui)"}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 pt-2 sm:pt-0 border-t border-slate-800/80 sm:border-0 justify-between sm:justify-end">
                              {remoteUrl ? (
                                <a
                                  href={remoteUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`remote-modem-link-${s.name}`}
                                  title={`Remote modem: ${remoteUrl}`}
                                  className="flex-1 sm:flex-none justify-center px-3 py-2 sm:px-2 sm:py-0.5 rounded-md text-[11px] sm:text-[10px] font-semibold flex items-center gap-1 bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 transition-colors"
                                >
                                  <ExternalLink className="w-3 h-3" /> Remot Modem
                                </a>
                              ) : <span className="flex-1 sm:hidden" />}
                              {canManage && (
                              <button
                                onClick={() => handleTogglePppoe(s.name, s.disabled)}
                                disabled={togglingId === s.name}
                                className={`ml-4 sm:ml-0 px-3 py-2 sm:px-2 sm:py-0.5 rounded-md text-[11px] sm:text-[10px] font-semibold flex items-center justify-center gap-1 disabled:opacity-50 flex-shrink-0 min-w-[84px] sm:min-w-0 ${
                                  s.disabled
                                    ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                                    : "bg-rose-500/20 text-rose-300 hover:bg-rose-500/30"
                                }`}
                                data-testid={`toggle-secret-${s.name}`}
                              >
                                {togglingId === s.name ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : s.disabled ? (
                                  <>
                                    <Unlock className="w-3 h-3" /> Buka
                                  </>
                                ) : (
                                  <>
                                    <Lock className="w-3 h-3" /> Isolir
                                  </>
                                )}
                              </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* Create Secret Modal */}
      {showCreateSecret && (
        <CreatePppoeSecretModal
          routerId={router.id}
          routerName={router.nama}
          onClose={() => setShowCreateSecret(false)}
          onSuccess={() => loadSnapshot()}
        />
      )}
    </div>
  );
};

export const RealRoutersList = ({ refreshKey, onDeleted }) => {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "finance";
  const [routers, setRouters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/jaringan/mikrotik/routers`, {
        withCredentials: true,
      });
      setRouters(res.data);
      // Preserve previous selection when possible, otherwise select first
      setSelectedId((prev) => {
        if (prev && res.data.some((r) => r.id === prev)) return prev;
        return res.data[0]?.id || null;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [refreshKey]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/jaringan/mikrotik/routers/${id}`, {
        withCredentials: true,
      });
      if (selectedId === id) setSelectedId(null);
      load();
      onDeleted?.();
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal hapus router");
    }
  };

  if (loading) {
    return (
      <div className="p-6 rounded-2xl bg-[#0e1628] border border-slate-800 text-center text-xs text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2 text-cyan-400" />
        Memuat daftar router real...
      </div>
    );
  }

  if (routers.length === 0) {
    return (
      <div
        className="p-8 rounded-2xl bg-[#0e1628] border border-slate-800 border-dashed text-center"
        data-testid="empty-real-routers"
      >
        <Server className="w-10 h-10 mx-auto text-slate-600 mb-2" />
        <p className="text-sm font-bold text-slate-300">Belum ada router real yang terhubung</p>
        <p className="text-xs text-slate-500 mt-1">
          Klik <span className="text-cyan-400 font-semibold">"Tambah Router"</span> untuk mendaftarkan MikroTik Anda.
        </p>
      </div>
    );
  }

  const selectedRouter = routers.find((r) => r.id === selectedId) || routers[0];

  return (
    <div className="space-y-3" data-testid="real-routers-list">
      {/* Dropdown selector when >= 2 routers */}
      {routers.length > 1 && (
        <div className="relative" data-testid="router-selector-wrapper">
          <label className="text-[11px] text-slate-400 font-semibold block mb-1.5">
            Pilih Router untuk Ditampilkan ({routers.length} terdaftar)
          </label>
          <button
            onClick={() => setDropdownOpen((o) => !o)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[#0e1628] border border-slate-700 hover:border-cyan-500/60 transition-colors text-left"
            data-testid="router-selector-btn"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  selectedRouter.last_status === "online" ? "bg-emerald-400" : "bg-rose-400"
                }`}
              />
              <div className="min-w-0">
                <p className="text-xs font-bold text-white truncate">{selectedRouter.nama}</p>
                <p className="text-[10px] font-mono text-slate-400 truncate">
                  {selectedRouter.host}:{selectedRouter.port} • {selectedRouter.last_identity || "—"}
                </p>
              </div>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${
                dropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {dropdownOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
              <div
                className="absolute z-50 mt-1 w-full rounded-xl bg-[#0e1628] border border-slate-700 shadow-2xl overflow-hidden max-h-80 overflow-y-auto"
                data-testid="router-selector-dropdown"
              >
                {routers.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => {
                      setSelectedId(r.id);
                      setDropdownOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      r.id === selectedId
                        ? "bg-cyan-500/10 border-l-2 border-cyan-400"
                        : "hover:bg-slate-800/60 border-l-2 border-transparent"
                    }`}
                    data-testid={`router-option-${r.id}`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        r.last_status === "online" ? "bg-emerald-400" : "bg-rose-400"
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-white truncate">{r.nama}</p>
                      <p className="text-[10px] font-mono text-slate-500 truncate">
                        {r.host}:{r.port}
                        {r.last_identity && ` • ${r.last_identity}`}
                        {r.last_board && ` • ${r.last_board}`}
                      </p>
                    </div>
                    {r.id === selectedId && (
                      <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Selected router detail */}
      {selectedRouter && (
        <RouterDetailPanel
          key={selectedRouter.id}
          router={selectedRouter}
          onRefreshList={load}
          onEdit={(r) => setEditing(r)}
          onDelete={handleDelete}
          canManage={canManage}
        />
      )}

      {/* Edit modal */}
      {editing && (
        <AddMikrotikRouterModal
          initialData={editing}
          onClose={() => setEditing(null)}
          onSuccess={() => load()}
        />
      )}
    </div>
  );
};
